<template>
  <Dropzone
    v-slot="{ active }"
    class="z-0 p-5 bg-background-light-110 rounded-4 relative"
    :disabled="hasFile"
    @files="addFiles"
  >
    <StripedBox
      border-color="var(--color-red-10)"
      border-radius="4px"
      class="box cursor-pointer"
      :class="{ active }"
      :active="!value"
      :aspect="aspect"
      @click.prevent.stop="!hasFile && emit('click')"
    >
      <div class="drop-box w-full h-full">
        <Box v-if="value" :aspect="1" use-height>
          <inline-file
            :file="value"
            :thumbnail="thumbnail"
            :thumbnail-size="{
              width: 300,
              height: 300,
            }"
            @file="hasFile = !!$event"
            @remove="removeFile"
          />
        </Box>
        <Box
          v-else
          :aspect="1"
          class="placeholder"
          use-height
        >
          <div class="flex flex-col gap-y-5 items-center">
            <component :is="icon" v-if="icon" class="m-auto w-9.5" />
            <CloudIcon v-else class="m-auto w-9.5" />
            <span class="">
              {{ label ?? $t('chooseFile') }}
            </span>
          </div>
        </Box>
      </div>
    </StripedBox>
  </Dropzone>
</template>

<script lang="ts" setup>
import { ref, type Component } from 'vue';
import CloudIcon from '@images/cloud.svg?component';
import { useVModel } from '@vueuse/core';
import Box from '../Box.vue';
import Dropzone from '../Dropzone.vue';
import type { WbFile } from '@/entities/Entities';
import StripedBox from '../StripedBox.vue';
import { Upload, type FileUpload } from '../upload/fileUpload';
import InlineFile from '../upload/InlineFile.vue';

export interface Props {
  acceptedFiles?: string;
  aspect?: number;
  label?: string;
  icon?: Component;
  modelValue?: FileUpload<WbFile>;
  thumbnail?: boolean;
}

const props = withDefaults(defineProps<Props>(), { thumbnail: true });

const emit = defineEmits(['update:modelValue', 'click']);
const value = useVModel(props, 'modelValue', emit, { passive: true });

const hasFile = ref<boolean>(false);

const addFiles = (files: File[]) => {
  if (!files.length) return;
  const upload = Upload.from<WbFile>(files[0]);
  value.value = upload;
};

const removeFile = () => {
  value.value = undefined;
  hasFile.value = false;
};
</script>

<style lang="scss" scoped>
.placeholder :deep(.box-content) {
  @apply flex justify-center items-center h-full pointer-events-none;
  @apply text-red-10;
  @apply select-none;
}

:deep(.box.active) {
  --stripe-box-border-color: var(--color-text-dark-100);
}
</style>
